/**
 * @file ElementStyleGroup.tsx
 * @description Вторая группа панели элемента секции (phase19.16, SB122;
 * uiux-settings-groups §4.3; SB120 этап 3а — uiux-sb120-stage3 §2):
 *  - виды с `typo` (реестр shared `TYPO_PART_KEYS`) — «Цвет и шрифт»: цвет,
 *    шрифт, размер и разрядка по частям (`ElementTypeFields`), у кнопки
 *    перед ними — заливка и рамка (`ButtonColorFields`, реестр `STYLE_EXTRA`);
 *    сводка «свой #F4EBDD · Lora 56»; «!» — контраст, мелкий H1, больше 3
 *    шрифтов, «✕» — свой шрифт удалён или замечание проверки;
 *  - таблица и вопросы (этап 3б) — перед частями фон шапки, полосы и
 *    линии / разделитель и значок (реестр `STYLE_EXTRA`);
 *  - «Товары» и «Похожие» — группы нет: строка «Шрифт и цвет карточек — в
 *    Каталоге →» в «Содержимом» (TD-436);
 *  - остальные виды с текстом — «Шрифт» с одной справкой;
 *  - элемент без текста — группы нет.
 */

import {
  DEFAULT_SITE_THEME,
  elementFontParts,
  isTypoElementKind,
  type SectionBg,
  type SectionElement,
  type SectionElementOf,
  type TypoElementKind,
} from "@chaiberi/shared";
import { useMemo, type ComponentType } from "react";

import { SettingsGroup } from "../../../ui/settings-groups";
import { useShopFonts } from "../../../site-view/shop-fonts-context";
import { useSectionForm } from "../section-form-context";
import { ButtonColorFields } from "./ButtonColorFields";
import { ElementFontNote } from "./ElementFontNote";
import { ElementTypeFields } from "./ElementTypeFields";
import { FaqLookFields } from "./FaqLookFields";
import { TableLookFields } from "./TableLookFields";
import { useElementFonts } from "../../../../hooks/site-builder/useElementFonts";
import { fontSummary, typoStatus, typoSummary } from "../../../../lib/site-builder/element-groups";
import { typoOf, withTypo } from "../../../../lib/site-builder/element-typo";
import { sectionBgSample } from "../../../../lib/site-builder/text-style";
import { headingSizePresets, TYPO_BASE } from "../../../../lib/site-builder/typo-base";
import { typoFieldContext } from "../../../../lib/site-builder/typo-kind-meta";
import { ELEMENT_STYLE_FIELDS } from "../../../../lib/site-builder/issue-groups";

/** Id группы в панели элемента. */
export const STYLE_GROUP_ID = "style";

/** Поля вида перед частями — реестром (OCP): кнопка — заливка и рамка; таблица и вопросы — линии и фоны (3б). */
interface StyleExtraProps<K extends TypoElementKind> {
  element: SectionElementOf<K>;
  onChange: (element: SectionElementOf<K>) => void;
  path: string;
}
const STYLE_EXTRA: { readonly [K in TypoElementKind]?: ComponentType<StyleExtraProps<K>> } = {
  button: ButtonColorFields,
  table: TableLookFields,
  faq: FaqLookFields,
};

/**
 * Карточки товаров оформляет «Каталог → Карточка товара» (этап 5): у «Товаров»
 * и «Похожих» своей группы нет — строка-переход в «Содержимом» (TD-436).
 */
const CATALOG_CARD_KINDS: ReadonlySet<SectionElement["kind"]> = new Set(["products", "product-related"]);

/** Виды, у которых размер подсказывается ступенями заголовков. */
const HEADING_PRESET_PARTS: Readonly<Partial<Record<TypoElementKind, string>>> = { heading: "main", "product-title": "title" };

interface Props {
  element: SectionElement;
  onChange: (element: SectionElement) => void;
  /** Фон под элементом: свой фон колонки, подложка или фон секции (`elementBg`). */
  bg: SectionBg;
  /** Путь элемента: `columns.items.0.elements.2`. */
  path: string;
  /** Подпись возврата во «Шрифтах»: «Кнопка (секция «Новинки»)». */
  backLabel: string;
  /** Есть замечание проверки по полям группы. */
  invalid?: boolean;
}

export function ElementStyleGroup({ element, onChange, bg, path, backLabel, invalid = false }: Props) {
  const { theme, elementWarnings } = useSectionForm();
  const { registry } = useShopFonts();
  // Части текста зависят от формы элемента (у «Надписи» — от стиля).
  const parts = useMemo(() => elementFontParts(element), [element]);
  const fonts = useElementFonts(parts);
  const note = <ElementFontNote parts={parts} backLabel={backLabel} />;
  const keyed = parts.filter((p) => p.key !== undefined);
  if (CATALOG_CARD_KINDS.has(element.kind)) return null;

  if (isTypoElementKind(element.kind) && keyed.length > 0) {
    const kind = element.kind;
    const colors = (theme ?? DEFAULT_SITE_THEME).colors;
    const ctx = typoFieldContext(element, sectionBgSample(bg, colors), colors);
    const typo = typoOf(element);
    const Extra = STYLE_EXTRA[kind] as ComponentType<StyleExtraProps<TypoElementKind>> | undefined;
    const presetPart = HEADING_PRESET_PARTS[kind];
    const owner = element as unknown as Readonly<Record<string, unknown>>;
    return (
      <SettingsGroup
        id={STYLE_GROUP_ID}
        title="Цвет и шрифт"
        summary={typoSummary(keyed, typo, theme ?? DEFAULT_SITE_THEME, registry)}
        status={typoStatus(elementWarnings, ELEMENT_STYLE_FIELDS.map((f) => `${path}.${f}`), invalid)}
      >
        {Extra ? <Extra element={element as SectionElementOf<TypoElementKind>} onChange={onChange} path={path} /> : null}
        <ElementTypeFields
          parts={keyed}
          typo={typo}
          onChange={(next) => onChange(withTypo(element, next))}
          path={path}
          baseOf={(key) => TYPO_BASE[kind](owner, key, theme)}
          presetsOf={(key) => (key === presetPart ? headingSizePresets(theme) : undefined)}
          elementBg={ctx.sample}
          colorAutoLabel={ctx.autoLabel}
          colorAuto={ctx.autoColor}
          colorHint={ctx.hint}
        />
        {note}
      </SettingsGroup>
    );
  }
  // Без цвета текста — одна справка о шрифте; текста нет — группы нет.
  if (!fonts) return null;
  return (
    <SettingsGroup id={STYLE_GROUP_ID} title="Шрифт" summary={fontSummary(fonts.lines, fonts.theme)} status={invalid ? "error" : undefined}>
      {note}
    </SettingsGroup>
  );
}
